import { Clock, Users } from 'lucide-react';
import { Link } from 'react-router';
import type { ExerciseCard as Card } from '@train2volley/shared';
import { Badge } from '@/components/ui/badge';
import { VisibilityBadge } from '@/components/visibility-badge';
import { CARD_STATUS, DIFFICULTY, durationLabel, playersLabel } from '@/lib/exercise-labels';

/** Oefenkaart (design-skill: ExerciseCard). Onbekend = "–", nooit 0; geen thumbnails. */
export function ExerciseCard({ exercise: e }: { exercise: Card }) {
  const level = e.difficulty ? DIFFICULTY[e.difficulty] : null;
  return (
    <article className="group relative flex flex-col gap-3 rounded-xl border bg-card p-4 shadow-xs transition-[border-color,box-shadow] duration-120 hover:border-foreground/25 hover:shadow-md">
      <div className="flex flex-wrap items-center gap-2">
        {e.primaryCategory && <Badge variant="secondary">{e.primaryCategory.name}</Badge>}
        <VisibilityBadge value={e.visibility} />
        {e.status && e.status !== 'PUBLISHED' && (
          <Badge variant={e.status === 'REJECTED' ? 'destructive' : 'outline'}>
            {CARD_STATUS[e.status]}
          </Badge>
        )}
      </div>
      <h3 className="text-h4 font-semibold">
        {/* Hele kaart klikbaar via de link; één tabstop per kaart. */}
        <Link
          to={`/oefeningen/${e.id}`}
          className="outline-none after:absolute after:inset-0 after:rounded-xl focus-visible:after:ring-2 focus-visible:after:ring-ring"
        >
          {e.title}
        </Link>
      </h3>
      {e.summary && <p className="line-clamp-2 text-sm text-muted-foreground">{e.summary}</p>}
      <dl className="mt-auto flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground tabular-nums">
        <div className="flex items-center gap-1.5">
          <dt className="sr-only">Duur</dt>
          <Clock className="size-3.5" aria-hidden="true" />
          <dd>{durationLabel(e.durationMinutes)}</dd>
        </div>
        <div className="flex items-center gap-1.5">
          <dt className="sr-only">Spelers</dt>
          <Users className="size-3.5" aria-hidden="true" />
          <dd>{playersLabel(e.minPlayers, e.maxPlayers)}</dd>
        </div>
        {level && (
          <div className="flex items-center gap-1.5">
            <dt className="sr-only">Niveau</dt>
            <level.icon className="size-3.5" aria-hidden="true" />
            <dd>{level.label}</dd>
          </div>
        )}
      </dl>
    </article>
  );
}
